import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import { PurchaseOrder, PurchaseOrderItem } from '@domain/purchasing';
import { Icon, IconSize } from '@shared/icons';
import { RichTextField } from '@shared/ui/common/RichTextField';
import { Note } from '@domain/purchasing/models/Notes';
import { NoteBlock } from './NoteBlock';
import { useNotes } from '@features/purchasing/hooks';

interface PurchaseOrderNotesProps {
  purchaseOrder: PurchaseOrder;
}

export const PurchaseOrderNotes: React.FC<PurchaseOrderNotesProps> = ({
  purchaseOrder,
}) => {
  const theme = useTheme();

  const [isCreating, setIsCreating] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Use the notes hook to manage state
  const { notes, createNote, updateNote, deleteNote, isCreating: isCreatingNote } = useNotes({
    purchaseOrderId: purchaseOrder.id
  });

  const [noteContent, setNoteContent] = useState('');
  const [selectedItem, setSelectedItem] = useState<PurchaseOrderItem | undefined>(undefined);

  const handleSetNoteItem = (item: PurchaseOrderItem) => {
    setIsMenuOpen(false);
    setSelectedItem(item);
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setIsMenuOpen(true);
  };

  const handleCreateNote = async () => {
    
    if (!noteContent.trim()) return;

    try {
      await createNote(
        { content: noteContent, purchaseOrder: purchaseOrder, purchaseOrderItem: selectedItem },
      );
      setNoteContent('');
      setSelectedItem(undefined);

    } catch (error) {
      console.error('Failed to create note:', error);
    }
  };

  const handleUpdateNote = async (note: Note) => {
    if (!note.id) return;

    try {
      await updateNote(note.id, note);
    } catch (error) {
      console.error('Failed to update note:', error);
    }
  };

  const handleDeleteNote = async (note: Note) => {
    if (!note.id) return;

    try {
      await deleteNote(note.id);
    } catch (error) {
      console.error('Failed to delete note:', error);
    }
  };


  return (
    <>
      <Box sx={{ p: 0 }}>
        <Stack spacing={4}>

          {/* Create Note */}
          <Stack spacing={1}>
            <RichTextField
              onFocus={() => setIsCreating(true)}
              onBlur={() => setIsCreating(false)}
              value={noteContent}
              onChange={(value) => setNoteContent(value)}
              placeholder="Add notes about this purchase order..."
            />

            {isCreating && (
              <Stack direction="row" gap={2} alignItems="center" justifyContent="space-between" flex={1}>
                {purchaseOrder.items.length > 0 && (
                  selectedItem ? (
                    <Chip
                      icon={<Icon icon="LinkBroken" />}
                      onClick={() => setSelectedItem(undefined)}
                      label={
                        <Box sx={{
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          maxWidth: 150,
                        }}>
                          {selectedItem.product.title}
                        </Box>
                      }
                    />
                  ) : (
                    <Chip
                      onClick={handleOpenMenu}
                      icon={<Icon icon="LinkAngled" />}
                      label="Link item"
                    />
                  )
                )}

                <Stack direction="row" gap={2}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setIsCreating(false)}
                  >
                    Cancel
                  </Button>

                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleCreateNote}
                    disabled={!noteContent.trim() || isCreatingNote}
                  >
                    {isCreatingNote ? 'Adding...' : 'Add'}
                  </Button>
                </Stack>
              </Stack>
            )}
          </Stack>

          {/* Notes */}
          {notes && notes.map((note) => (
            <NoteBlock
              key={note.id}
              note={note}
              items={purchaseOrder.items}
              onUpdateNote={(updatedNote) => handleUpdateNote(updatedNote)}
              onDeleteNote={(noteToDelete) => handleDeleteNote(noteToDelete)}
            />
          ))}

          {notes.length === 0 && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
            }}>
              <Typography variant="body2" color="text.primary">
                Your notes are empty
              </Typography>
            </Box>
          )}
        </Stack>


      </Box>

      <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={() => setIsMenuOpen(false)}>
        {purchaseOrder.items.map((item) => (
          <MenuItem
            key={item.id}
            color="secondary"
            onClick={() => handleSetNoteItem(item)}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            <Box sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 150,
            }}>{item.product.title}</Box>
          </MenuItem>
        ))}
      </Menu>
    </>


  );
};
